import type { ReactNode } from "react";
import { cx } from "../cx";

/** success green, warning amber, emergency red, neutral grey (information / waiting), ink and ring (map legend). */
export type Tone = "success" | "warning" | "emergency" | "neutral" | "ink" | "ring";

export interface DotProps {
  tone: Tone;
  /** The soft halo ring around success, warning and emergency dots. */
  halo?: boolean;
  className?: string;
}

/** A bare decorative dot (aria-hidden). Always pair it with words: use StatusDot when the dot carries meaning. */
export function Dot({ tone, halo = true, className }: DotProps) {
  return <span aria-hidden="true" className={cx("lcc-dot", `lcc-dot--${tone}`, halo && "lcc-dot--halo", className)} />;
}

export interface StatusDotProps {
  tone: Tone;
  /** The words (never colour alone). */
  label: ReactNode;
  halo?: boolean;
  /** Hide the words visually when the same words are visible next to it (they stay for screen readers). */
  labelHidden?: boolean;
  className?: string;
}

/** Severity dot with its halo and the label: warning text in the darker amber, emergency in the darker red. */
export function StatusDot({ tone, label, halo = true, labelHidden, className }: StatusDotProps) {
  return (
    <span className={cx("lcc-status", `lcc-status--${tone}`, className)}>
      <Dot tone={tone} halo={halo} />
      <span className={cx("lcc-status__label", labelHidden && "lcc-sr-only")}>{label}</span>
    </span>
  );
}

export interface NoticeProps {
  tone?: Tone;
  title?: ReactNode;
  children?: ReactNode;
  /** `alert` for errors that need attention now; `status` (polite) by default. */
  role?: "status" | "alert" | "note";
}

/** A tile-grey notice with a dot: one-line explanations, "unavailable" rows, errors. */
export function Notice({ tone = "neutral", title, children, role = "status" }: NoticeProps) {
  return (
    <div className="lcc-notice" role={role === "note" ? "note" : role}>
      <Dot tone={tone} />
      <div className="lcc-notice__body">
        {title && <span className="lcc-notice__title">{title}</span>}
        {children && <span>{children}</span>}
      </div>
    </div>
  );
}

export interface EmptyStateProps {
  title: ReactNode;
  body?: ReactNode;
  action?: ReactNode;
}

export function EmptyState({ title, body, action }: EmptyStateProps) {
  return (
    <div className="lcc-empty">
      <p className="lcc-empty__title">{title}</p>
      {body && <p className="lcc-empty__body">{body}</p>}
      {action}
    </div>
  );
}
